/**
 * @fileoverview Menu component main React interface.
 */
goog.declareModuleId('yext.ui.components.menu.MenuController');

import asserts from 'goog:goog.asserts';

import {DropdownTrigger} from '/ui/components/dropdowntrigger/dropdowntrigger';
import {MenuAction2} from '/ui/components/menu/action/menuaction';
import {ComboTrigger} from '/ui/components/menu/trigger/combotrigger';
import {Action} from '/ui/components/menu/types/action';
import {Config, NestingMode} from '/ui/components/menu/types/config';
import {FilterBar} from '/ui/components/select/filterbar/filterbar';
import {SelectList} from '/ui/components/select/standard/selectlist';
import {classNames} from '/ui/lib/classnames';

import * as styles from '/ui/components/menu/menu.module.scss';
import ArrowRightSvg from '/ui/icons/inline/arrow-right.svg';

/** @param {Action} action */
function validateAction(action) {
  asserts.assertString(action.name, 'Action name missing');
  asserts.assert(action.callback || action.href || action.actions, 'Action requires callback, href, or actions');
  if (action.callback) {
    asserts.assertFunction(action.callback, 'Action callback is not a function');
    asserts.assert(!action.openNewTab, 'Action openNewTab can only be specified with href');
  } else if (action.href) {
    asserts.assertString(action.href, 'Action href is not a string');
  }
}

/**
 *
 * @param {!Array<!Action>} actions
 * @param {string} term
 * @returns {!Array<!Action>}
 */
function filterActions(actions, term) {
  return /** @type {!Array<!Action>} */ (
    actions.map(action => filterAction(action, term))
      .filter(action => action != null)
  );
}

/**
 *
 * @param {string} str
 * @param {string} subStr
 * @returns {boolean} Whether str contains subStr, case-insensitive
 */
function caseInsensitiveContains(str, subStr) {
  return str.toLowerCase().indexOf(subStr.toLowerCase()) !== -1;
}

/**
 * @param {!Action} action
 * @param {string} term
 * @returns {?Action}
 */
function filterAction(action, term) {
  const textContents = [
    action.name,
    action.secondaryText || '',
  ];
  if (textContents.some(tc => caseInsensitiveContains(tc, term))) {
    return action;
  }
  if (!action.actions) {
    return null;
  }
  const matchingChildren = filterActions(action.actions, term);
  if (matchingChildren.length == 0) {
    return null;
  }
  const filteredAction = /** @type {!Action} */(Object.assign({}, action));
  filteredAction.actions = matchingChildren;
  return filteredAction;
}

export class MenuController extends React.Component {
  /** @param {Config} props */
  constructor(props) {
    super(props);

    /** @type {!Config} */
    this.props;

    asserts.assertArray(props.actions, 'Actions is not an array');
    asserts.assert(props.actions.length > 0, 'No actions specified');
    props.actions.forEach(validateAction);

    this.state = {
      dropdownExpanded: false,
      searchTerm: '',
    };

    this.onToggleDropdown = this.onToggleDropdown.bind(this);
    this.onCloseDropdown = this.onCloseDropdown.bind(this);

    this.unfilteredHeight = /** @type {!React.MutableRefObject<number|undefined>} */ (React.createRef());
  }

  componentDidUpdate() {
    this.props.actions.forEach(validateAction);
  }

  onToggleDropdown() {
    this.setState(state => ({dropdownExpanded: !state.dropdownExpanded, searchTerm: ''}));
    this.props.onDropdownStateChange && this.props.onDropdownStateChange(!this.state.dropdownExpanded);
  }

  onCloseDropdown() {
    this.setState({dropdownExpanded: false, searchTerm: ''});
    this.props.onDropdownStateChange && this.props.onDropdownStateChange(false);
  }

  render() {
    /** @type {?} */
    const Trigger = this.props.showDefaultAction ? ComboTrigger : DropdownTrigger;
    const defaultAction = this.props.actions[0];
    const actions = this.props.showDefaultAction
          ? this.props.actions.slice(1)
          : this.props.actions;
    const shouldCascade = this.props.nestingMode === NestingMode.CASCADE && !this.state.searchTerm;

    const filteredActions = filterActions(actions, this.state.searchTerm);

    return (
      <Trigger
        defaultAction={defaultAction}
        isContextual={this.props.isContextual}
        alignMenu={this.props.alignMenu}
        ariaRole="listbox"
        onToggleDropdown={this.onToggleDropdown}
        onCloseDropdown={this.onCloseDropdown}
        dropdownExpanded={this.state.dropdownExpanded}
        label={this.props.label}
        disabled={this.props.disabled}
        primary={this.props.primary}
        ghost={this.props.ghost}
        ref={this.props.dropdownButtonRef}
        trigger={this.props.trigger}
        triggerClass={this.props.dropdownTriggerClass}
        allowContainerOverflow={this.props.allowContainerOverflow}
        ariaLabel={this.props.ariaLabel}
        tid={this.props.tid}
        pendoId={this.props.pendoId}
        wrapperClassName={this.props.wrapperClassName}
      >
        {
          this.props.enableSearch
            && <FilterBar
              searchTerm={this.state.searchTerm}
              onSearch={searchTerm => this.setState({searchTerm})}
            />
        }
        {shouldCascade
          ? <CascadeList
            isCascade={false}
            actions={filteredActions}
            onCloseFunc={this.onCloseDropdown}
            isVisible={this.state.dropdownExpanded}
            height={this.state.searchTerm ? this.unfilteredHeight.current : undefined}
            listHeightCallback={h => {
              if (!this.state.searchTerm) {
                this.unfilteredHeight.current = h;
              }
            }}
          />
          : <GroupedList
            actions={filteredActions}
            onCloseFunc={this.onCloseDropdown}
            separated={!this.state.searchTerm}
            isVisible={this.state.dropdownExpanded}
            height={this.state.searchTerm ? this.unfilteredHeight.current : undefined}
            listHeightCallback={h => {
              if (!this.state.searchTerm) {
                this.unfilteredHeight.current = h;
              }
            }}
          />
        }
      </Trigger>);
  }
}

/**
 * @param {{
 *   actions: !Array<!Action>,
 *   onCloseFunc: (function(): void),
 *   separated: boolean,
 *   isVisible: boolean,
 *   height?: (number|undefined),
 *   listHeightCallback?: (function((number|undefined)): void),
 * }} props
 * @returns {React.ReactElement}
 */
function GroupedList({actions, onCloseFunc, separated, isVisible, height, listHeightCallback}) {
  const rootActions = actions.filter(o => (!o.actions || o.actions.length === 0));
  const sections = actions.filter(o => (o.actions && o.actions.length !== 0));
  return (
    <SelectList
      tabIndex="-1"
      isCascade={false}
      hasCascade={false}
      isVisible={isVisible}
      height={height}
      listHeightCallback={listHeightCallback}
    >
      {rootActions.map((action, index) => (
        <MenuItem key={action.name + index} action={action}>
          <MenuAction2
            key={action.name + index}
            action={action}
            onCloseFunc={onCloseFunc}
          />
        </MenuItem>
      ))}
      {sections.map((section, index) => (
        <GroupedSection
          key={section.name + index}
          section={section}
          onCloseFunc={onCloseFunc}
          separated={separated}
        />
      ))}
    </SelectList>
  );
}

/**
 * TODO(UX-2303): Use the group role
 * @param {{
 *   section: !Action,
 *   onCloseFunc: (function(): void),
 *   separated: boolean,
 * }} props
 * @returns {React.ReactElement}
 */
function GroupedSection({section, onCloseFunc, separated}) {
  return (
    <>
      <li role="presentation" className={separated ? styles.groupSeparated : undefined}>
        <div className={styles.groupTitle}>{section.displayName || section.name}</div>
      </li>
      {section.actions?.map((action, index) => (
        <MenuItem key={action.name + index} action={action}>
          <MenuAction2
            key={action.name + index}
            action={action}
            onCloseFunc={onCloseFunc}
          />
        </MenuItem>
      ))}
    </>
  );
}

/**
 * @param {{
 *   actions: !Array<!Action>,
 *   onCloseFunc: (function(): void),
 *   isCascade: boolean,
 *   isVisible: boolean,
 *   height?: (number|undefined),
 *   listHeightCallback?: (function((number|undefined)): void),
 * }} props
 * @returns {React.ReactElement}
 */
function CascadeList({actions, onCloseFunc, isCascade, isVisible, height, listHeightCallback}) {
  return (
    <SelectList
      tabIndex={-1}
      isCascade={isCascade}
      hasCascade={actions.some(a => !!a.actions)}
      isVisible={isVisible}
      height={height}
      listHeightCallback={listHeightCallback}
    >
      {actions.map((action, index) => (
        <MenuItem key={action.name + index} action={action}>
          {action.actions
            && <CascadeList isCascade={true} actions={action.actions} onCloseFunc={onCloseFunc} isVisible={isVisible}/>}
          <MenuAction2
            action={action}
            onCloseFunc={onCloseFunc}
          />
          {action.actions && <ArrowRightSvg className={styles.cascadeArrow} />}
        </MenuItem>
      ))}
    </SelectList>
  );
}

/**
 * @param {{
 *   action: !Action,
 *   children?: React.ReactNode,
 * }} props
 * @returns {React.ReactElement}
 */
function MenuItem({action, children}) {
  const className = classNames(
    'ui-menu-item',
    'js-menu-item',
    'js-select-option',
    {
      'ui-menu-item-disabled': action.disabled,
      'js-select-option-disabled': action.disabled,
    },
  );
  return (
    <li className={className} key={action.name}>
      {children}
    </li>
  );
}
